import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { TableRow, TableCell, Select, MenuItem } from '@mui/material'

import SimpleTable from 'components/ui/SimpleTable'
import CreatableSelect from 'components/form/CreatableSelect'
import DraggableContainer from 'components/ui/DraggableContainer'
import { getDefaultTeeBoxOptions } from 'utils/getDefaultTeeBoxOptions'
import { TrashIcon } from 'assets/icons'
import { removeNonNumericCharacters } from 'utils/numbers'
import SearchButton from '../SearchButton'
import { sendErrorNotification } from 'utils/notifications'
import { fetchCourseById } from 'store/course/thunks'
import { useAppDispatch } from 'store'
import { rem } from 'theme/materialUITheme'

interface Props {
  version: CourseVersion | null
  syncTeeBoxes: (teeBoxes: CourseTeeBox[]) => void
  syncDefaultTees?: (defaultTees: CourseDefaultTees) => void
  isModifiable: boolean
  secondaryVersion?: CourseVersion | null
  syncMappingData?: (mappingData: CourseTeeBoxMapping[]) => void
  isCourseEditorUser?: boolean
}

export default function CourseTeeBoxes({
  version,
  syncTeeBoxes,
  syncDefaultTees,
  isModifiable,
  secondaryVersion,
  syncMappingData,
  isCourseEditorUser = false,
}: Props): JSX.Element {
  const dispatch = useAppDispatch()
  const [teeBoxes, setTeeBoxes] = useState<CourseTeeBox[]>([])
  const [teeBoxesAdded, setTeeBoxesAdded] = useState<boolean>(false)
  const [secondaryTeeBoxes, setSecondaryTeeBoxes] = useState<CourseTeeBox[]>([])
  const [secondaryTeeBoxesAdded, setSecondaryTeeBoxesAdded] = useState<boolean>(false)
  const [teeBoxMappingData, setTeeBoxMappingData] = useState<CourseTeeBoxMapping[]>([])
  const [teeBoxesDeleted, setTeeBoxedDeleted] = useState<boolean>(false)
  const [menDefaultTeeIndex, setMenDefaultTeeIndex] = useState<number>(1)
  const [womenDefaultTeeIndex, setWomenDefaultTeeIndex] = useState<number>(1)
  const defaultTeeSelectionDisabled = teeBoxes.length < 3
  const [copyFromCourse, setCopyFromCourse] = useState<number | null>(null)

  const intl = useIntl()
  const crMin = 10.0
  const crMax = 99.9
  const slopeMin = 55.0
  const slopeMax = 155.0

  useEffect(() => {
    if (syncDefaultTees) {
      syncDefaultTees({
        menIndex: menDefaultTeeIndex,
        womenIndex: womenDefaultTeeIndex,
      })
    }
  }, [syncDefaultTees, menDefaultTeeIndex, womenDefaultTeeIndex])

  useEffect(() => {
    if (teeBoxes.length === 1) {
      setMenDefaultTeeIndex(0)
      setWomenDefaultTeeIndex(0)
    } else if (teeBoxes.length === 2) {
      setMenDefaultTeeIndex(0)
      setWomenDefaultTeeIndex(1)
    } else {
      setMenDefaultTeeIndex(1)
      setWomenDefaultTeeIndex(teeBoxes.length - 1)
    }
  }, [teeBoxes])

  useEffect(() => {
    const versionChanged =
      (teeBoxes.length > 0 && teeBoxes[0].version && teeBoxes[0].version !== version?.version) ||
      (teeBoxes.length === 0 && version && teeBoxesDeleted === false)

    if (version && version.teeBoxes && (!teeBoxesAdded || versionChanged)) {
      setTeeBoxes(version.teeBoxes)
      setTeeBoxesAdded(true)
      setTeeBoxedDeleted(false)
      setWomenDefaultTeeIndex(version.teeBoxes.length - 1)
    }
  }, [version, teeBoxes, teeBoxesAdded, teeBoxesDeleted])

  useEffect(() => {
    const secondaryVersionChanged =
      secondaryTeeBoxes.length > 0 &&
      secondaryTeeBoxes[0].version &&
      secondaryTeeBoxes[0].version !== secondaryVersion?.version

    if (secondaryVersion && secondaryVersion.teeBoxes && (!secondaryTeeBoxesAdded || secondaryVersionChanged)) {
      setSecondaryTeeBoxes(secondaryVersion.teeBoxes)
      setSecondaryTeeBoxesAdded(true)
    }
  }, [secondaryVersion, secondaryTeeBoxes, secondaryTeeBoxesAdded])

  useEffect(() => {
    syncTeeBoxes(teeBoxes)
  }, [teeBoxes, syncTeeBoxes])

  const columns: TableColumn[] = [
    {
      id: 'newTeeBox',
      label: secondaryVersion
        ? intl.formatMessage({ id: 'strings.newTeeBox' })
        : intl.formatMessage({ id: 'strings.tees' }),
      width: secondaryVersion ? 25 : 30,
    },
    {
      id: 'menCr',
      label: intl.formatMessage({ id: 'strings.mens' }) + ' ' + intl.formatMessage({ id: 'strings.cr' }),
      width: 4.75,
    },
    {
      id: 'menSlopeDivider',
      label: '',
      width: 0.5,
    },
    {
      id: 'menSlope',
      label: intl.formatMessage({ id: 'strings.mens' }) + ' ' + intl.formatMessage({ id: 'strings.slope' }),
      width: 4.75,
    },
    {
      id: 'menDefaultTee',
      label: intl.formatMessage({ id: 'strings.mens' }) + ' ' + intl.formatMessage({ id: 'strings.defaultTee' }),
      width: 13,
    },
    {
      id: 'womenCr',
      label: intl.formatMessage({ id: 'strings.ladies' }) + ' ' + intl.formatMessage({ id: 'strings.cr' }),
      width: 4.75,
    },
    {
      id: 'womenSlopeDivider',
      label: '',
      width: 0.5,
    },
    {
      id: 'womenSlope',
      label: intl.formatMessage({ id: 'strings.ladies' }) + ' ' + intl.formatMessage({ id: 'strings.slope' }),
      width: 4.75,
    },
    {
      id: 'womenDefaultTee',
      label: intl.formatMessage({ id: 'strings.ladies' }) + ' ' + intl.formatMessage({ id: 'strings.defaultTee' }),
      width: 13,
    },
    {
      id: 'delete',
      label: '',
      width: 24,
    },
    {
      id: 'oldTeeBox',
      label: intl.formatMessage({ id: 'strings.oldTeeBox' }),
      width: 25,
    },
  ]

  const getColumns = () => {
    return secondaryVersion
      ? columns.filter((col: TableColumn) => col.id !== 'delete')
      : columns.filter((col: TableColumn) => col.id !== 'oldTeeBox')
  }

  const moveTeeBox = useCallback(
    (dragIndex, hoverIndex) => {
      const dragItem = teeBoxes[dragIndex]
      const orderedTeeBoxes = teeBoxes.slice()
      orderedTeeBoxes.splice(dragIndex, 1)
      orderedTeeBoxes.splice(hoverIndex, 0, dragItem)
      setTeeBoxes(orderedTeeBoxes)
    },
    [teeBoxes],
  )

  const getTeeBoxOptions = () => {
    const courseTeeBoxes = teeBoxes.map((tb: CourseTeeBox) => tb.name)
    const allTeeBoxOptions = courseTeeBoxes
      .concat(getDefaultTeeBoxOptions())
      .sort()
      .filter(function (name, index, arr) {
        return arr.indexOf(name) === index
      })
    return allTeeBoxOptions.map((name: string) => {
      return { key: name, label: name }
    })
  }

  const modifyTeeBoxes = (index: number, field: string, value: string | number | null) => {
    setTeeBoxes(
      [...teeBoxes].map((tb: CourseTeeBox, i: number) => {
        if (i !== index) {
          return tb
        }
        return {
          ...tb,
          [field]: value,
        }
      }),
    )
  }

  const handleNameChange = (index: number, value: string | number | null) => {
    modifyTeeBoxes(index, 'name', value)
  }

  const parseValue = (isSlope: boolean, value: string) => {
    return isSlope ? parseInt(value) : parseFloat(value)
  }

  const handleTeeBoxValueChange = (e: any, field: string) => {
    const index = parseInt(e.target.getAttribute('data-index'))
    let value = e.target.value.replace(',', '.')
    if ((field === 'slopeMen' || field === 'slopeWomen') && value !== '-') {
      value = removeNonNumericCharacters(value)
    }
    const lastCharacter = value[value.length - 1]
    const shouldConvertToFloat = ['.'].includes(lastCharacter) === false

    // - or valid numbers are accepted
    const regex = /^(-|\d+(?:\.\d*)?)$/
    const isValid = regex.test(value)
    if (value && !isValid) {
      return
    }

    if (shouldConvertToFloat) {
      if (value === '-') {
        value = -1
      }
      const isSlope = field.startsWith('slope')
      value = value ? parseValue(isSlope, value) : ''

      let max = crMax

      if (isSlope) {
        max = slopeMax
      }

      if (value && value > max) {
        return
      }
    }

    modifyTeeBoxes(index, field, value)
  }

  const handleTeeBoxMappingChange = (e: any, index: number) => {
    const data: CourseTeeBoxMapping = {
      newTeeBoxId: teeBoxes[index].id,
      oldTeeBoxId: e.target.value === '' ? undefined : e.target.value,
    }

    const duplicateIndex = teeBoxMappingData.map((tb: CourseTeeBoxMapping) => tb.newTeeBoxId).indexOf(data.newTeeBoxId)
    if (duplicateIndex >= 0) {
      teeBoxMappingData.splice(duplicateIndex, 1)
    }

    const newData = [...teeBoxMappingData, data]
    setTeeBoxMappingData(newData)
    if (syncMappingData) {
      syncMappingData(newData)
    }
  }

  const addTeeBox = () => {
    const newTeeBox: CourseTeeBox = {
      id: 0,
      name: '',
      version: 0,
      crMen: -1,
      slopeMen: -1,
      crWomen: -1,
      slopeWomen: -1,
    }
    setTeeBoxes([...teeBoxes, newTeeBox])
  }

  const removeTeeBox = (e: React.MouseEvent<SVGElement>) => {
    const dataIndex = e.currentTarget.getAttribute('data-index')
    if (dataIndex) {
      const index = parseInt(dataIndex)
      const newTeeBoxes = teeBoxes.slice()
      newTeeBoxes.splice(index, 1)
      if (newTeeBoxes.length === 0) {
        setTeeBoxedDeleted(true)
      }
      setTeeBoxes(newTeeBoxes)
    }
  }

  const copyTeeBoxes = async () => {
    try {
      if (!copyFromCourse) {
        return
      }

      // Fetch a course by ID with no side-effects to store
      const course = await dispatch(fetchCourseById(copyFromCourse)).unwrap()

      // Get official version of the course
      const teeBoxes = course.versions?.find((v) => v.isOfficial)?.teeBoxes || []

      // Replace current tee boxes with copied ones, change their version to 0
      setTeeBoxes(
        teeBoxes.map((tb) => ({
          ...tb,
          version: 0,
        })),
      )
    } catch (error) {
      console.error('Error copying tee boxes:', error)
      sendErrorNotification(`Course with ID ${copyFromCourse} not found.`)
    }
  }

  const getInitialName = (tb: CourseTeeBox) => {
    return {
      key: tb.name,
      label: tb.name,
    }
  }

  const getFieldValue = (value: number) => {
    if (value === -1) {
      return '-'
    }
    return value
  }

  const isDisabled = () => {
    return (!isModifiable || hasSecondaryVersion()) && version !== null
  }

  const hasSecondaryVersion = () => {
    return secondaryVersion !== null && secondaryVersion !== undefined
  }

  const handleDefaultTeeBoxChange = (index: number, type: 'men' | 'women') => {
    const isMenDefault = type === 'men'
    const action = isMenDefault ? setMenDefaultTeeIndex : setWomenDefaultTeeIndex
    action(index)
  }

  const renderTeeBoxNameSelect = (tb: CourseTeeBox, i: number) => {
    const name = `newTeeBox-${i}`
    if (hasSecondaryVersion()) {
      return (
        <MapSelect name={name} value={tb.id} variant="outlined" disabled>
          {teeBoxes.map((tb: CourseTeeBox, index: number) => {
            return (
              <MenuItem key={`${name}-${index}`} value={tb.id}>
                {tb.name}
              </MenuItem>
            )
          })}
        </MapSelect>
      )
    }
    return (
      <DraggableContainer id={tb.id} index={i} moveItem={moveTeeBox}>
        <TeeBoxSelect
          name={name}
          options={getTeeBoxOptions()}
          initialValue={getInitialName(tb)}
          draggable
          onChange={handleNameChange}
          data-index={i}
          disabled={isDisabled()}
        />
      </DraggableContainer>
    )
  }

  const renderTeeBoxMappingSelect = (tb: CourseTeeBox, i: number) => {
    if (!secondaryVersion) {
      return ''
    }
    const name = `oldTeeBox-${i}`
    return (
      <MapSelect name={name} onChange={(e) => handleTeeBoxMappingChange(e, i)} variant="outlined">
        <ResetValue key="resetvalue" value="">
          {intl.formatMessage({ id: 'strings.reset' })}
        </ResetValue>
        {secondaryTeeBoxes.map((tb: CourseTeeBox, index: number) => {
          return (
            <MenuItem key={`${name}-${index}`} value={tb.id}>
              {tb.name}
            </MenuItem>
          )
        })}
      </MapSelect>
    )
  }

  const renderTeeBoxes = (): JSX.Element[] => {
    const rows = teeBoxes.map((tb: CourseTeeBox, i: number) => (
      <TableRow key={`teebox-tablerow-${i}`}>
        <TableCell>{renderTeeBoxNameSelect(tb, i)}</TableCell>
        <TableCell className="flex">
          <TeeBoxInput
            type="text"
            value={getFieldValue(tb.crMen)}
            onChange={(e) => handleTeeBoxValueChange(e, 'crMen')}
            data-index={i}
            min={crMin}
            max={crMax}
            disabled={isDisabled()}
          />
        </TableCell>
        <TableCell className="cell-divider">/</TableCell>
        <TableCell>
          <TeeBoxInput
            type="text"
            value={getFieldValue(tb.slopeMen)}
            onChange={(e) => handleTeeBoxValueChange(e, 'slopeMen')}
            data-index={i}
            min={slopeMin}
            max={slopeMax}
            disabled={isDisabled()}
          />
        </TableCell>
        <TableCell className="radio-container">
          <input
            type="radio"
            disabled={!isCourseEditorUser || i === womenDefaultTeeIndex || defaultTeeSelectionDisabled}
            checked={i === menDefaultTeeIndex}
            onClick={() => handleDefaultTeeBoxChange(i, 'men')}
          />
        </TableCell>
        <TableCell className="flex">
          <TeeBoxInput
            type="text"
            value={getFieldValue(tb.crWomen)}
            onChange={(e) => handleTeeBoxValueChange(e, 'crWomen')}
            data-index={i}
            min={crMin}
            max={crMax}
            disabled={isDisabled()}
          />
        </TableCell>
        <TableCell className="cell-divider">/</TableCell>
        <TableCell>
          <TeeBoxInput
            type="text"
            value={getFieldValue(tb.slopeWomen)}
            onChange={(e) => handleTeeBoxValueChange(e, 'slopeWomen')}
            data-index={i}
            min={slopeMin}
            max={slopeMax}
            disabled={isDisabled()}
          />
        </TableCell>
        <TableCell className="radio-container">
          <input
            type="radio"
            disabled={!isCourseEditorUser || i === menDefaultTeeIndex || defaultTeeSelectionDisabled}
            checked={i === womenDefaultTeeIndex}
            onClick={() => handleDefaultTeeBoxChange(i, 'women')}
          />
        </TableCell>

        {!isDisabled() && (
          <TableCell style={{ paddingRight: rem(24) }}>
            <DeleteTeeBox dataIndex={i} onClick={removeTeeBox} />
          </TableCell>
        )}

        {secondaryVersion && <TableCell>{renderTeeBoxMappingSelect(tb, i)}</TableCell>}
      </TableRow>
    ))

    return [...rows, maybeRenderAddTeeBoxButton(), maybeRenderCopyTeeBoxesFromAnotherCourseButton()]
  }

  const maybeRenderAddTeeBoxButton = (): JSX.Element => {
    if (isDisabled()) {
      return <></>
    }
    return (
      <TableRow key={'addTeeboxButtonRow'} className="add-teebox">
        <TableCell>
          <AddTeeBoxButton onClick={addTeeBox}>{intl.formatMessage({ id: 'strings.addNewTee' })}</AddTeeBoxButton>
        </TableCell>
      </TableRow>
    )
  }

  const maybeRenderCopyTeeBoxesFromAnotherCourseButton = (): JSX.Element => {
    if (isDisabled()) {
      return <></>
    }
    return (
      <TableRow key={'copyTeeboxButtonRow'} className="add-teebox">
        <TableCell>
          <CopyTeeBoxesBox>
            <p style={{ marginBottom: '10px' }}>
              {intl.formatMessage({ id: 'strings.copyTeeboxesFromAnotherCourse' })}
            </p>
            <div style={{ display: 'flex' }}>
              <TeeBoxInput
                placeholder={intl.formatMessage({ id: 'strings.courseId' })}
                type="number"
                style={{ width: '10rem' }}
                onChange={(e) => setCopyFromCourse(parseInt(e.target.value))}
              />
              <ActionButton
                disabled={!copyFromCourse}
                onClick={() => copyTeeBoxes()}
                text={intl.formatMessage({ id: 'strings.execute' })}
              />
            </div>
          </CopyTeeBoxesBox>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <TeeBoxesContainer className={!isCourseEditorUser ? 'padding' : 'margin'}>
      {!secondaryVersion && !isCourseEditorUser && (
        <SectionTitle>{intl.formatMessage({ id: 'strings.courseRatingSlope' })}</SectionTitle>
      )}
      <SimpleTable columns={getColumns()}>{renderTeeBoxes()}</SimpleTable>
    </TeeBoxesContainer>
  )
}

const TeeBoxesContainer = styled.div`
  &.padding {
    padding: 0 20px;
  }
  &.margin {
    margin-top: 30px;
  }

  & table {
    border: none;
  }

  & th {
    padding-left: 10px;
    white-space: nowrap;
    &:first-of-type {
      padding-left: 30px;
    }
  }

  & tbody tr {
    &:nth-of-type(even):not(.add-teebox) {
      background-color: ${(props) => props.theme.customPalette.navActiveBg};
    }
  }

  & td.MuiTableCell-root {
    padding: 0 5px;
    border: none;
  }
  & .MuiTableCell-body {
    height: 60px;
  }
  & .flex {
    display: flex;
    align-items: center;
    padding-right: 0;
    margin-bottom: -1px;

    & + * {
      padding-left: 0;
    }
  }
  & .cell-divider {
    font-size: 30px;
    font-weight: 300;
    padding: 0 !important;
    text-align: center;
    color: #cccccc;
  }
  & .radio-container {
    text-align: center;
    & > input {
      &:not(:disabled) {
        cursor: pointer;
      }
      accent-color: ${(props) => props.theme.palette.primary.main};
    }
  }
`

const SectionTitle = styled.h2`
  width: 100%;
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  color: ${(props) => props.theme.customPalette.darkGray2};
  margin-bottom: 25px;
`

const TeeBoxInput = styled.input`
  color: #424a4d;
  background-color: ${(props) => props.theme.customPalette.light};
  border: 1px solid;
  border-color: #cccccc;
  border-radius: 5px;
  margin: 0 5px;
  text-align: center;
  height: 38px;
  width: 58px;
  font-size: 16px;

  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

const AddTeeBoxButton = styled.button`
  cursor: pointer;
  width: 200px;
  height: 53px;
  background-color: #fff;
  color: ${(props) => props.theme.palette.primary.main};
  border: 2px solid ${(props) => props.theme.palette.primary.main};
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  margin-left: 20px;
  margin-top: 10px;

  &:hover {
    background-color: #fff;
    opacity: 0.75;
  }
  &:disabled {
    border-color: transparent;
  }
`

const ActionButton = styled(SearchButton)``

const CopyTeeBoxesBox = styled.div`
  border: 2px solid ${(props) => props.theme.palette.primary.main};
  border-radius: 8px;
  margin-left: 20px;
  margin-top: 10px;
  padding: 1rem 1.5rem;
`

const DeleteTeeBox = styled(TrashIcon)`
  cursor: pointer;
  &:hover {
    opacity: 0.75;
  }
`

const MapSelect = styled(Select)`
  height: 44px;
  background-color: ${(props) => props.theme.customPalette.lightGray};
  min-width: 120px;
`

const ResetValue = styled(MenuItem)`
  color: ${(props) => props.theme.customPalette.mediumGray};
`

const TeeBoxSelect = styled(CreatableSelect)`
  width: 200px;
  & .MuiInputBase-formControl {
    background-color: ${(props) => props.theme.customPalette.light};
    height: 40px;
    line-height: 40px;
    & input {
      padding: 0 6px !important;
    }
  }
`
